import { Outlet, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { SideBar } from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  removeToken,
  selectManager,
  removeCurrentCompany,
} from "../home/homeSlice";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import "../../i18n/i18n";
import { DepartmentSelector } from "../../components/departmentSelector";
import {
  loadDepartmentAsync,
  selectCurrentDepartment,
  getDepartmentsByManagerAsync,
  selectDepartmentsList,
  selectGetDepartmentsByManagerStatus,
  removeCurrentDepartment,
  selectCurrentDepartmentUuid,
  selectCurrentDepartmentStatus,
  setCurrentDepartment,
  getCurrentDepartment,
} from "./departments/departmentsSlice";
import { AccessErrorPage } from "../../components/NoAccessErrorPage";
import { billingsSlice } from "../home/billings/billingsSlice";
import { sepaDebitSlice } from "../home/billings/sepaDebitSlice";
import { badgesSlice } from "../home/cardsAndBadges/badges/badgesSlice";
import { cardsSlice } from "../home/cardsAndBadges/cards/cardsSlice";
import { driversSlice } from "../home/drivers/driversSlice";
import { fleetSlice } from "../home/fleet/fleetSlice";
import { tripSlice } from "../home/fleet/trips/tripsSlice";
import { notificationsSlice } from "../home/notifications/notificationsSlice";
import { rulesSlice } from "../home/rules/rulesSlice";
import { transactionsSlice } from "../home/transactions/transactionsSlice";
import { UsersSlice } from "../home/users/usersSlice";
import { vehiclesSlice } from "../home/vehicles/vehiclesSlice";
import { departmentsSlice } from "../home/departments/departmentsSlice";

export const HomeDepartment = () => {
  const manager = useAppSelector(selectManager);
  const location = useLocation();
  const initialSelectedRoute = location.pathname.split("/")[2]
    ? location.pathname.split("/")[2]
    : "";
  const [selectedRoute, setSelectedRoute] = useState<string | undefined>(
    initialSelectedRoute,
  );
  const [fullScreen, setFullScreen] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const departments = useAppSelector(selectDepartmentsList);
  const getDepartmentsByManagerStatus = useAppSelector(
    selectGetDepartmentsByManagerStatus,
  );
  const currentDepartment = useAppSelector(selectCurrentDepartment);
  const currentDepartmentUuid = useAppSelector(selectCurrentDepartmentUuid);
  const currentDepartmentStatus = useAppSelector(selectCurrentDepartmentStatus);

  //RESET DES STATES DES SLICES LORSQUE LA COMPAGNIE CHANGE
  useEffect(() => {
    if (currentDepartmentStatus === "success" && currentDepartment) {
      void dispatch(
        sepaDebitSlice.actions.resetGetMemobankMandatePerCompanyStatus(),
      );
      void dispatch(UsersSlice.actions.resetManagersListStatus());
      void dispatch(cardsSlice.actions.resetCardsListStatus());
      void dispatch(badgesSlice.actions.resetBadgesList());
      void dispatch(rulesSlice.actions.resetRulesList());

      //VEHICLES
      void dispatch(vehiclesSlice.actions.resetListAllVehiclesInDbStatus());
      void dispatch(departmentsSlice.actions.resetDepartmentsListStatus());

      //FLEET
      void dispatch(fleetSlice.actions.resetFleetListStatus());
      void dispatch(fleetSlice.actions.resetDriversHistoryListStatus());
      void dispatch(fleetSlice.actions.resetVehicleSessionsListStatus());
      void dispatch(fleetSlice.actions.resetRestitutionPicturesStatus());
      void dispatch(tripSlice.actions.resetPlanningsListStatus());
      void dispatch(tripSlice.actions.resetTripsListStatus());

      //DRIVERS
      void dispatch(driversSlice.actions.resetDriversListStatus());
      void dispatch(
        driversSlice.actions.resetDriversAccessRequestsListStatus(),
      );

      //TRANSACTIONS
      void dispatch(transactionsSlice.actions.resetTransactionsListStatus());
      void dispatch(transactionsSlice.actions.resetAuthorizationsListStatus());
      void dispatch(transactionsSlice.actions.resetAsfTransactionsListStatus());

      //BILLINGS
      void dispatch(billingsSlice.actions.resetStatementsListStatus());
      void dispatch(billingsSlice.actions.resetBankStatementsListStatus());
      void dispatch(billingsSlice.actions.resetDepositsListStatus());
      void dispatch(billingsSlice.actions.resetAsfInvoicesListStatus());
      void dispatch(billingsSlice.actions.resetInvoicesListStatus());

      //NOTIFICATIONS
      void dispatch(notificationsSlice.actions.resetNotificationsListStatus());
    }
  }, [currentDepartment, currentDepartmentStatus, dispatch]);

  useEffect(() => {
    if (manager && getDepartmentsByManagerStatus === "idle") {
      void dispatch(
        getDepartmentsByManagerAsync({
          departmentUuid: manager.departmentsAccess?.[0] || "",
        }),
      );
    }
  }, [getDepartmentsByManagerStatus, dispatch, manager]);

  useEffect(() => {
    if (currentDepartmentStatus === "idle") {
      dispatch(getCurrentDepartment());
    }
  }, [currentDepartmentStatus, dispatch, manager]);

  useEffect(() => {
    if (
      getDepartmentsByManagerStatus === "success" &&
      departments.length > 0 &&
      currentDepartmentStatus === "success"
    ) {
      if (!currentDepartmentUuid) {
        const firstDepartment = departments[0];
        dispatch(
          setCurrentDepartment({ departmentUuid: firstDepartment.uuid }),
        );
        void dispatch(
          loadDepartmentAsync({ departmentUuid: firstDepartment.uuid }),
        );
      } else if (currentDepartmentUuid && !currentDepartment) {
        void dispatch(
          loadDepartmentAsync({ departmentUuid: currentDepartmentUuid }),
        );
      }
    }
  }, [
    getDepartmentsByManagerStatus,
    departments,
    currentDepartmentUuid,
    currentDepartment,
    currentDepartmentStatus,
    dispatch,
  ]);

  useEffect(() => {
    if (initialSelectedRoute !== selectedRoute) {
      setSelectedRoute(initialSelectedRoute);
    }
  }, [initialSelectedRoute, selectedRoute]);

  const onChangeRoute = useCallback(
    (route: string) => {
      setSelectedRoute(route);
      navigate(route);
    },
    [navigate],
  );

  const onChangeDepartment = useCallback(
    (departmentUuid: string) => {
      dispatch(setCurrentDepartment({ departmentUuid }));
      void dispatch(loadDepartmentAsync({ departmentUuid: departmentUuid }));
    },
    [dispatch],
  );

  const logout = useCallback(() => {
    dispatch(removeToken());
    dispatch(removeCurrentCompany());
    dispatch(removeCurrentDepartment());
    navigate("/logout");
  }, [dispatch, navigate]);

  useEffect(() => {
    if (manager && manager.status !== "ACTIVE") {
      logout();
    }
  }, [logout, manager]);

  type EnvType = "production" | "staging" | "demo" | "development";
  const env = import.meta.env["VITE_TARGET_ENV"] as EnvType;

  if (getDepartmentsByManagerStatus === "success" && departments.length === 0) {
    return <AccessErrorPage pageType="noDepartmentsAccess" />;
  }

  const props = {
    sections: [
      {
        title: t("home.menu.section.home.title"),
        routes: [
          {
            route: "",
            title: t("home.menu.home"),
          },
        ],
      },
      {
        title: t("home.menu.section.expenses.title"),
        routes: [
          {
            route: "transactions",
            title: t("home.menu.transactions"),
          },
          {
            route: "cards",
            title: t("home.menu.cards"),
          },
        ],
      },
      {
        title: t("home.menu.section.fleet.title"),
        routes: [
          {
            route: "vehicles",
            title: t("home.menu.vehicles"),
          },
          {
            route: "fleet",
            title: t("home.menu.fleet"),
          },
          {
            route: "drivers",
            title: t("home.menu.drivers"),
          },
        ],
      },
    ],
    route: "departments",
    selectedRoute,
    onChangeRoute,
    logout,
    companies: [],
    currentCompany: currentDepartment?.name ?? "",
    onChangeCompany: () => {
      onChangeDepartment(currentDepartmentUuid || "");
    },
    onClickCompanyCreation: () => {},
    hasAnalyticsAccess: currentDepartment?.hasAnalytics ?? false,
    hasAdvancedAnalyticsAccess:
      currentDepartment?.hasAdvancedAnalytics ?? false,
    canOrderBadges: false,
    isInProduction: env === "production",
    isInDemo: env === "demo",
    canCreateCompany: false,
    hasGeolocationAccess: false,
  };

  const departmentSelectorProps = {
    canCreateDepartment: false,
    departments: departments.map((dept) => ({
      name: dept.name,
      uuid: dept.uuid,
    })),
    onClickDepartmentCreation: () => {},
    currentDepartment: currentDepartmentUuid || "",
    onChangeDepartment: onChangeDepartment,
    size: "L" as const,
  };

  return (
    <StyledContainer>
      <StyledContent>
        <StyledSideBar $fullScreen={fullScreen}>
          <SideBar {...props}>
            <DepartmentSelector
              {...departmentSelectorProps}
            ></DepartmentSelector>
          </SideBar>
        </StyledSideBar>
        <Outlet context={{ setFullScreen }} />
      </StyledContent>
    </StyledContainer>
  );
};

const StyledSideBar = styled.div<{ $fullScreen: boolean }>`
  width: ${({ $fullScreen }) =>
    $fullScreen
      ? "0rem"
      : "17.125rem"}; /*because side bar menu si always open*/
`;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: ${colors["colors/accent/900"]};
`;

const StyledContent = styled.div`
  display: flex;
  flex: 1;
  height: 100vh;
`;
